import { Suspense, useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { CompanySettingsProvider, useCompanySettings } from './CompanySettingsContext';
import { CatalogProvider } from './CatalogContext';
import { LocaleProvider, useLocale } from './i18n/LocaleContext';
import { AiAssistantWorkerSurfaceProvider, useAiAssistantWorkerSurface } from './contexts/AiAssistantWorkerSurfaceContext';
import { AiFabSuppressionProvider, useIsAiFabSuppressed, useIsAiFabSuppressedUnlessDocked } from './contexts/AiFabSuppressionContext';
import AiFloatingFab from './components/ai/AiFloatingFab';
import useAiAssistant from './hooks/useAiAssistant';
import useProactiveSuggestion from './hooks/useProactiveSuggestion';
import { useTenantTimezone } from './hooks/tenant/useTenantTimezone';
import ProtectedRoute from './components/layout/ProtectedRoute';
import PlatformRoute from './components/platform/PlatformRoute';
import { saveLastRoute } from './utils/ui/lastRouteStorage';
import { openRecordOnTop } from './utils/ui/recordOpeners';
import { isBootGateExemptPath, shouldBootGateBlock } from './utils/ui/bootGateExemptPaths';
import { pageEntranceAnimation } from './utils/ui/pageEntranceAnimation';
import useReducedMotion from './hooks/ai/useReducedMotion';
import ErrorBoundary from './components/common/ErrorBoundary';
import BootRouteSplashTrigger from './ui/splash/BootRouteSplashTrigger';
import { SplashProvider, useSplash } from './ui/splash/SplashContext';
import SessionScopedShell from './ui/session/SessionScopedShell';
import SplashOverlay from './ui/splash/SplashOverlay';
import useIosKeyboardDismiss from './hooks/ui/useIosKeyboardDismiss';
import useScrollTapGuard from './hooks/ui/useScrollTapGuard';
import useInlineKeyboardGuard from './hooks/ui/useInlineKeyboardGuard';
import KeyboardDebugOverlay from './components/common/KeyboardDebugOverlay';
import { useResponsiveLayout } from './hooks/ui/useResponsiveLayout';
import { phonePagePadding, tabletPagePadding } from './ui/pageGutter';
import LogJobRouteDoor from './pages/LogJobRouteDoor';
import LogJobRouteFallback from './pages/LogJobRouteFallback';
import { UNSCHEDULED_FLOW_STATE_KEY } from './components/worker/unscheduledVisit/unscheduledFlowShared';
import DashboardLoadSkeleton from './components/common/DashboardLoadSkeleton';
import useSessionTreeKey from './hooks/auth/useSessionTreeKey';
import { useAuthHandoffHold } from './ui/splash/authHandoffHold';
import { lazyWithRetry as lazy } from './utils/ui/lazyWithRetry';
import publicRoutes from './routes/publicRoutes';
import { ldLog } from './utils/ui/loginDebug';

const AiFloatingPanel = lazy(() => import('./components/ai/AiFloatingPanel'));
const UpdateAvailableBanner = lazy(() => import('./components/common/UpdateAvailableBanner'));

const AppShell = lazy(() => import('./pages/AppShell'));
const LogJob = lazy(() => import('./pages/LogJob'));
const JobDetailPage = lazy(() => import('./pages/JobDetailPage'));
const PlatformShell = lazy(() => import('./pages/PlatformShellEntry'));

function deriveAiRouteState(messages, routeStops, followUpCount) {
  const hour = new Date().getHours();
  const hasFU = (followUpCount ?? 0) > 0;
  const listFromMsgs = [...messages].reverse().find(
    (m) => m?.action?.type === 'customer_list' && m?.action?.rows?.length,
  );
  if (listFromMsgs?.action?.rows?.length) {
    const rows = listFromMsgs.action.rows;
    const done = rows.filter((r) => r.status === 'done').length;
    const pending = rows.filter((r) => r.status === 'pending').length;
    const pend = rows.find((r) => r.status === 'pending');
    return {
      total: rows.length,
      done,
      pending,
      hasFollowUps: hasFU,
      isMorning: hour < 12,
      isEndOfDay: hour >= 16,
      firstStopName: rows[0]?.name || '',
      nextPendingName: pend?.name || '',
      logsToday: done,
    };
  }
  if (routeStops?.length) {
    const done = routeStops.filter((r) => r.completed).length;
    return {
      total: routeStops.length,
      done,
      pending: routeStops.length - done,
      hasFollowUps: hasFU,
      isMorning: hour < 12,
      isEndOfDay: hour >= 16,
      firstStopName: '',
      nextPendingName: '',
      logsToday: done,
    };
  }
  return null;
}

// Leaf-route chunk-load fallbacks. These sit BELOW the boot-splash Suspense so
// that while the LogJob / JobDetail JS chunk downloads, the user sees a layout
// skeleton (looks like the page is opening) instead of the CrewVex wordmark.
// The chunk is also prefetched from the boards (prefetchLogJobChunk), so in
// practice this fallback rarely renders past the first cold open.
function JobDetailRouteFallback() {
  const { isMobile } = useResponsiveLayout();
  return <DashboardLoadSkeleton variant="generic" kpiCount={0} showHero listRows={4} padding={isMobile ? phonePagePadding('16px', '16px') : tabletPagePadding('16px', '16px')} isMobile={isMobile} />;
}

// Per-route mount animation. Which entrance plays — and WHY returning to a
// shell no longer rises — lives in pageEntranceAnimation.js. Keyed at the call
// site so each cross-shell navigation remounts and re-fires, while in-route
// re-renders keep the captured value (no spurious re-fire).
let didFirstPageIn = false;
function PageTransition({ transitionKey, still = false, children }) {
  const reduced = useReducedMotion();
  const animRef = useRef(null);
  if (animRef.current === null) {
    animRef.current = pageEntranceAnimation({ transitionKey, isFirstPageIn: !didFirstPageIn, reduced, still });
    didFirstPageIn = true;
  }
  return (
    <div style={{ animation: animRef.current, minHeight: '100%' }}>
      {children}
    </div>
  );
}

function CrewVexRoutesAndAiAssistant() {
  const intl = useIntl();
  const { routeStops, followUpCount, workerSurface } = useAiAssistantWorkerSurface();
  const { sessionUser, role, loading: authLoading } = useAuth();
  const { settings, loading: settingsLoading } = useCompanySettings();
  const navigate = useNavigate();
  const location = useLocation();
  const aiEnabled = settings?.aiEnabled === true;
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1280),
  );
  const isAiNarrowViewport = viewportWidth < 1024;
  const fabRef = useRef(null);
  const tz = useTenantTimezone();
  const [panelEverOpened, setPanelEverOpened] = useState(false);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const displayName = sessionUser?.user_metadata?.full_name
    || sessionUser?.email?.split('@')[0]
    || '';

  const {
    messages, loading, panelOpen,
    sendMessage, stopResponse, resetChat, executeAction, approveAction, dismissAction, confirmFollowUpAction, confirmLifecycleAction, confirmSendSummaryAction, confirmEodCloseoutAction, requestPostJobSummaryFromCta, togglePanel, closePanel,
    uploadVoiceBlob,
    errorState, setError, clearError,
    streamingText,
    isStreaming, streamPhase,
    onRowAction, onNextStopLog, onQuickLogSubmit, onQuickLogCancel, onQuickLogUndo, onQuickLogVoiceToggle, quickLogRecordingId, quickLogRecordingSeconds,
    onInlineChipTap,
  } = useAiAssistant({
    userId: sessionUser?.id ?? null,
    role,
    userName: displayName,
    onNavigate: (path, opts) => navigate(path, opts),
    pathname: location.pathname,
    tz,
    locale: intl.locale,
    voiceMessageLabel: intl.formatMessage({ id: 'aiAssistant.voiceMessage', defaultMessage: 'Voice message' }),
  });

  useEffect(() => {
    if (panelOpen) setPanelEverOpened(true);
  }, [panelOpen]);

  const handleViewJobFromAi = useCallback((jobId) => {
    if (!jobId) return;
    // KAN-1186: opens ON TOP of the current page when the home has an opener; route is the fallback.
    openRecordOnTop('job', jobId).then((handled) => { if (!handled) navigate(`/jobs/${jobId}`); });
    closePanel();
  }, [navigate, closePanel]);

  // Detect current page context for proactive suggestions
  const path = location.pathname;
  const customerIdFromUrl = path.startsWith('/log-job/') ? path.split('/')[2]
    : null;
  const currentPage = path.includes('/route') || path.includes('/schedule') ? 'route'
    : path.startsWith('/log-job/') ? 'log_job'
    : path === '/' || path.includes('/dashboard') || path.includes('/home') ? 'dashboard'
    : 'other';

  const routeState = useMemo(
    () => deriveAiRouteState(messages, routeStops, followUpCount),
    [messages, routeStops, followUpCount],
  );

  const handleNextStopSendSummaries = useCallback(() => {
    sendMessage(intl.formatMessage({ id: 'chip.sendAllSummaries' }));
  }, [sendMessage, intl]);

  const { suggestion, hasUrgentSuggestion } = useProactiveSuggestion({
    role,
    customerId: customerIdFromUrl,
    currentPage,
    routeStops,
    followUpCount,
    workerSurface,
    intl,
  });

  const fabSuppressed = useIsAiFabSuppressed();
  const fabSuppressedUnlessDocked = useIsAiFabSuppressedUnlessDocked();
  // The FAB is the AI entry for admin/supervisor/billing AND worker (workers get
  // route-stop chips via AiAssistantWorkerSurfaceContext + a worker branch in the
  // ai-assistant edge fn — do NOT gate this on FEATURE_ACCESS['ai-hub'], which is
  // the hub TAB only and excludes worker). Operations is the sole exclusion: the
  // AI edge fns have no operations gatherer yet and would fall through unhandled.
  const aiChromeAvailable = !authLoading && !!sessionUser && !settingsLoading && aiEnabled && role !== 'operations';
  // KAN-1011: the button stays MOUNTED while overlays are open (hidden by prop) so a
  // docked one can dim with the bar and never re-mounts late. The panel keeps the
  // old rule: any overlay unmounts it.
  const showAiChrome = aiChromeAvailable && !fabSuppressed && !fabSuppressedUnlessDocked;

  // Collapse each shell's paths to ONE transition key so navigation keeps the
  // shell mounted instead of remounting + refetching all data (instant feel).
  // `/app/:feature` (tenant shell) and ALL of `/platform/...` (platform shell,
  // INCLUDING /platform/tenants/:id — the detail is the right pane of the
  // unified tenants workspace, so selecting a tenant must not remount the shell)
  // each collapse to a constant key. Leaf pages and login key by pathname.
  const pageTransitionKey = location.pathname.startsWith('/app')
    ? 'app'
    : location.pathname.startsWith('/platform/')
      ? 'platform'
      : location.pathname;

  // Log Job entered from the unscheduled-visit flow pushes its own body in under a
  // header that must not move (KAN-765), so the whole-page rise is skipped.
  const stillEntrance = location.pathname.startsWith('/log-job/') && Boolean(location.state?.[UNSCHEDULED_FLOW_STATE_KEY]);

  return (
    <>
      <Suspense fallback={<BootRouteSplashTrigger />}>
      <PageTransition key={pageTransitionKey} transitionKey={pageTransitionKey} still={stillEntrance}>
      <Routes>
        {publicRoutes()}
        {/* RD-NAV-001: all four tenant roles live under the feature-based /app
            shell. Legacy role-home URLs redirect in (bookmarks, email links,
            session-resume). */}
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/supervisor" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/admin" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/billing" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/app" element={
          <ProtectedRoute allowedRoles={['admin', 'billing', 'worker', 'supervisor', 'operations']}>
            <AppShell />
          </ProtectedRoute>
        } />
        <Route path="/app/:feature/*" element={
          <ProtectedRoute allowedRoles={['admin', 'billing', 'worker', 'supervisor', 'operations']}>
            <AppShell />
          </ProtectedRoute>
        } />
        {/* Operations is READ-ONLY on job records — it CANNOT start logging a
            job, so it is excluded from the Log Job route (RLS + the operations
            job-write revoke are the real boundary; this stops a deep-link /
            stale-bookmark from reaching the logging flow). Keep admin /
            supervisor / worker. */}
        <Route path="/log-job/:customerId" element={
          <ProtectedRoute allowedRoles={['worker', 'supervisor', 'admin']}>
            <Suspense fallback={<LogJobRouteFallback />}>
              <LogJobRouteDoor>
                <LogJob />
              </LogJobRouteDoor>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/jobs/:jobId" element={
          <ProtectedRoute allowedRoles={['worker', 'supervisor', 'admin', 'billing', 'operations']}>
            <Suspense fallback={<JobDetailRouteFallback />}>
              <JobDetailPage />
            </Suspense>
          </ProtectedRoute>
        } />
        {/* RD-PLAT-001: platform super-admin surface — URL-driven feature routes
            (separate shell — cross-tenant, super_admin, MFA-gated). 2b: the
            tenant detail is the right pane of the unified /platform/tenants
            master-detail, so /platform/tenants/:id renders PlatformHome too. */}
        <Route path="/platform" element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="/platform/tenants/:tenantId/:detailTab?" element={
          <PlatformRoute>
            <PlatformShell />
          </PlatformRoute>
        } />
        <Route path="/platform/:feature/*" element={
          <PlatformRoute>
            <PlatformShell />
          </PlatformRoute>
        } />
<Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </PageTransition>
      </Suspense>
      {aiChromeAvailable && (
        <>
          <AiFloatingFab
            ref={fabRef}
            onClick={togglePanel}
            hasSuggestion={hasUrgentSuggestion}
            panelOpen={panelOpen}
            busy={Boolean(loading || isStreaming)}
            hidden={fabSuppressed}
            hiddenUnlessDocked={fabSuppressedUnlessDocked}
          />
          {showAiChrome && panelEverOpened && (
            <Suspense fallback={null}>
              <AiFloatingPanel
                isOpen={panelOpen}
                onClose={closePanel}
                fabRef={fabRef}
                messages={messages}
                loading={loading}
                onSend={sendMessage}
                onStop={stopResponse}
                onReset={resetChat}
                onExecuteAction={executeAction}
                onApprove={approveAction}
                onDismiss={dismissAction}
                onConfirmFollowUp={confirmFollowUpAction}
                onConfirmLifecycle={confirmLifecycleAction}
                onConfirmSendSummary={confirmSendSummaryAction}
                onConfirmEodCloseout={confirmEodCloseoutAction}
                onPostJobSummaryAction={requestPostJobSummaryFromCta}
                onRowAction={onRowAction}
                onQuickLogSubmit={onQuickLogSubmit}
                onQuickLogCancel={onQuickLogCancel}
                onQuickLogUndo={onQuickLogUndo}
                onQuickLogVoiceToggle={onQuickLogVoiceToggle}
                quickLogRecordingId={quickLogRecordingId}
                quickLogRecordingSeconds={quickLogRecordingSeconds}
                role={role}
                isMobile={isAiNarrowViewport}
                uploadVoiceBlob={uploadVoiceBlob}
                errorState={errorState}
                onSetError={setError}
                onClearError={clearError}
                suggestion={suggestion}
                isStreaming={isStreaming}
                streamingText={streamingText}
                streamPhase={streamPhase}
                routeState={routeState}
                catalog={null}
                onNextStopLog={onNextStopLog}
                onNextStopSendSummaries={handleNextStopSendSummaries}
                onInlineChipTap={onInlineChipTap}
                onViewJob={handleViewJobFromAi}
              />
            </Suspense>
          )}
        </>
      )}
    </>
  );
}

function LastRouteTracker() {
  const location = useLocation();
  const { sessionUser, role, loading, tenantLoading } = useAuth();

  useEffect(() => {
    if (loading || tenantLoading || !sessionUser || !role) return;
    // PUBLIC ROUTES ARE NEVER A "LAST ROUTE" (RD-EST-RESP-001). `/estimate`
    // carries a bearer token in `location.search` that React Router still sees
    // AFTER the page's own `history.replaceState` (the router observes
    // `popstate`, not `replaceState`), and a signed-in visitor opening a
    // customer's link would otherwise persist it. `saveLastRoute` refuses it on
    // its own now; this ties "public route" to the ONE allowlist rather than
    // two, so a future public page inherits the behaviour.
    if (isBootGateExemptPath(location.pathname)) return;
    saveLastRoute(location.pathname, location.search, role);
  // Keyed on sessionUser?.id, not the whole object: avoids re-running on every
  // TOKEN_REFRESHED (which replaces the sessionUser ref). Only presence/id matters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, sessionUser?.id, role, loading, tenantLoading]);

  return null;
}

// Remounts the routes + AI chrome when a user LEAVES (sign-out or A → B), alongside
// useAiAssistant's userId reset, so no React state survives a user switch. Sign-in, and a
// sign-out that never left the sign-in route, keep the tree so Login animates (useSessionTreeKey).
function KeyedRoutesAndAi() {
  const { sessionUser } = useAuth();
  const { pathname } = useLocation();
  return <CrewVexRoutesAndAiAssistant key={useSessionTreeKey(sessionUser?.id, pathname)} />;
}

// Single app-level boot barrier. Signals the singleton splash overlay to
// cover the app until auth is resolved AND the active locale is the
// confirmed final one (DB pref for signed-in users, stored/browser for
// anonymous). Prevents:
//   • first-visit language flash (render in 'en' then flip to org default)
//   • post-signin ping-pong (anonymous locale → orgDefault → DB pref)
//   • Login form flashing between authLoading=false and sessionUser propagating
// `immediate: true` skips the overlay's flash delay (preloader → boot stays covered);
// returning null keeps guarded children from flashing their initial state behind it.
// PUBLIC-ROUTE EXEMPTION (RD-EST-RESP-001): `/estimate` has no session/react-intl to feed,
// so blocking it on auth + the EN catalog costs a revenue surface seconds on a phone. The rule
// is the pure `shouldBootGateBlock` (utils/ui/bootGateExemptPaths.js) so it is
// unit-tested without mounting the app — and its allowlist is an EXACT match,
// never a prefix test, because a route exempted by accident would render with
// `authLoading` still true in the shell all five roles share.
function BootGate({ children }) {
  const { loading: authLoading } = useAuth();
  const { localeReady, messagesReady, englishMessages } = useLocale();
  const { pathname } = useLocation();
  const handoffHold = useAuthHandoffHold(); // Login's sign-in morph (authHandoffHold.js)
  const blocking = shouldBootGateBlock({ pathname, authLoading, localeReady, messagesReady, englishReady: Boolean(englishMessages), handoffHold });
  useEffect(() => { ldLog('bootgate', { blocking, authLoading, localeReady, messagesReady, hold: handoffHold }); }, [blocking, authLoading, localeReady, messagesReady, handoffHold]);
  useSplash(blocking, { immediate: true });
  if (blocking) return null;
  return children;
}

// SessionScopedShell (provider stack + root docks + toast-store session clear)
// lives in src/ui/session/SessionScopedShell.js — extracted 2026-08-15 for the
// 500-line cap. The BootGate/KeyedRoutesAndAi tree stays HERE and rides in as
// children (they are App.js-local components).

function App() {
  useIosKeyboardDismiss();
  useScrollTapGuard();
  useInlineKeyboardGuard();
  return (
    <SplashProvider>
      <SplashOverlay />
      <AuthProvider>
        <CompanySettingsProvider>
          {/* CatalogProvider reads usePriceVisibility → must sit inside CompanySettingsProvider (RD-ROLE-005). */}
          <CatalogProvider>
          <LocaleProvider>
          <ErrorBoundary>
          {/* KAN-1019: React Router 7 wraps location updates in startTransition, so the header (urgent state) could commit before the tab content; sync updates keep them in one frame. */}
          <BrowserRouter useTransitions={false}>
            <AiAssistantWorkerSurfaceProvider>
            <AiFabSuppressionProvider>
            <LastRouteTracker />
            <SessionScopedShell>
              <BootGate>
                <KeyedRoutesAndAi />
              </BootGate>
            </SessionScopedShell>
            {/* Global update notice — appears when /version.json reports a newer deploy.
                Lazy-loaded so it doesn't bloat the boot bundle. Sits above everything via z-index. */}
            <Suspense fallback={null}>
              <UpdateAvailableBanner />
            </Suspense>
            {/* Keyboard-reframe diagnostic chip — renders ONLY with ?kbdebug=1
                on the URL (device debugging; see keyboardReframeDebug.js). */}
            <KeyboardDebugOverlay />
            </AiFabSuppressionProvider>
            </AiAssistantWorkerSurfaceProvider>
        </BrowserRouter>
          </ErrorBoundary>
          </LocaleProvider>
          </CatalogProvider>
        </CompanySettingsProvider>
      </AuthProvider>
    </SplashProvider>
  );
}

export default App;
